// src/utils/deferScriptLoading.ts

/**
 * Utility for deferring the loading of large JavaScript libraries
 */

// Track libraries that have been loaded
const loadedLibraries = new Map<string, Promise<any>>();

declare global {
    interface Window {
        pdfjsLib: any;
    }
}

/**
 * Load PDF.js only when needed
 * @returns Promise that resolves when PDF.js is loaded
 */
export const loadPdfJs = (): Promise<any> => {
    if (loadedLibraries.has('pdfjs')) {
        return loadedLibraries.get('pdfjs')!;
    }

    const loadPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.type = 'module';
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/5.4.530/pdf.min.mjs';
        script.async = true;
        script.defer = true;
        script.onerror = () => reject(new Error('Failed to load PDF.js'));
        script.onload = () => {
            // PDF.js v5 exports are easier to handle via global or import
            // But for a script tag, we wait for it to be available
            if (window.pdfjsLib) {
                window.pdfjsLib.GlobalWorkerOptions.workerSrc =
                    'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/5.4.530/pdf.worker.min.mjs';
                resolve(window.pdfjsLib);
            } else {
                // Handle cases where it might not be immediately on window
                setTimeout(() => {
                    if (window.pdfjsLib) resolve(window.pdfjsLib);
                    else reject(new Error('PDF.js loaded but not found on window'));
                }, 100);
            }
        };
        document.body.appendChild(script);
    });

    loadedLibraries.set('pdfjs', loadPromise);
    return loadPromise;
};

/**
 * Check if user is likely to need PDF features
 * @returns True if PDF access is likely based on URL or access patterns
 */
export const isPdfAccessLikely = (): boolean => {
    // Check URL for PDF-related paths
    const isPdfUrl =
        window.location.pathname.includes('/pdf') ||
        window.location.pathname.includes('/document') ||
        window.location.pathname.includes('/view');

    // Check for file extension in URL
    const hasPdfParam =
        window.location.search.includes('.pdf') || window.location.pathname.endsWith('.pdf');

    return isPdfUrl || hasPdfParam;
};

interface ScriptOptions {
    async?: boolean;
    defer?: boolean;
    id?: string;
    crossOrigin?: string;
    integrity?: string;
}

/**
 * Asynchronously load JS file
 * @param src - Script URL to load
 * @param options - Additional options
 * @returns Promise that resolves when script is loaded
 */
export const loadScript = (src: string, options: ScriptOptions = {}): Promise<void> => {
    const { async = true, defer = true, id } = options;

    if (id && loadedLibraries.has(id)) {
        return loadedLibraries.get(id)!;
    }

    const loadPromise = new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = async;
        script.defer = defer;

        if (options.crossOrigin) {
            script.crossOrigin = options.crossOrigin;
        }

        if (options.integrity) {
            script.integrity = options.integrity;
        }

        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Failed to load script: ${src}`));

        document.body.appendChild(script);
    });

    if (id) {
        loadedLibraries.set(id, loadPromise);
    }

    return loadPromise;
};

/**
 * Preload script to be used later
 * @param src - Script URL to preload
 */
export const preloadScript = (src: string): void => {
    if (document.querySelector(`link[rel="preload"][href="${src}"]`)) {
        return; // Already preloaded
    }

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'script';
    link.href = src;
    document.head.appendChild(link);
};

/**
 * Initialize script loading optimizations
 */
export const initScriptLoading = (): void => {
    // Preload PDF.js if user is likely to use it soon
    if (isPdfAccessLikely()) {
        preloadScript('https://cdnjs.cloudflare.com/ajax/libs/pdf.js/5.4.530/pdf.min.mjs');
    }

    // Handle file selector module loading only when needed
    document.addEventListener(
        'click',
        (e: Event) => {
            // Check if user is interacting with file inputs or upload buttons
            const target = e.target as HTMLElement;
            const isFileInput =
                (target as HTMLInputElement).type === 'file' ||
                target.closest('input[type="file"]') ||
                target.closest('[data-file-upload]');

            if (isFileInput && !loadedLibraries.has('fileSelector')) {
                loadScript('/node_modules/file-selector/src/file.js', { id: 'fileSelector' });
            }
        },
        { passive: true }
    );
};
